import React from 'react';
import { Link } from 'react-router-dom';
import usePlan, { getPlanTheme } from '@/hooks/usePlan';
import { Crown, Sparkles, Zap, Shield } from 'lucide-react';

const ICONS = {
  free: Zap,
  starter: Sparkles,
  professional: Shield,
  enterprise: Crown,
};

/**
 * Compact, plan-aware badge. Used in navbar, sidebar and anywhere the
 * user's current tier should be surfaced. Size variants:
 *   `sm`  — h-5, no name
 *   `md`  — h-6 with name
 *   `lg`  — h-8 bold, with name and subtitle slot
 */
export default function PlanBadge({ size = 'md', showName = true, planKey: forceKey, className = '', as = 'span', ...rest }) {
  const { planKey: livePlan } = usePlan();
  const planKey = forceKey || livePlan;
  const theme = getPlanTheme(planKey);
  const Icon = ICONS[theme.key] || Sparkles;

  const sizeCls = {
    sm: 'h-5 px-2 text-[9px] tracking-[0.16em] gap-1',
    md: 'h-6 px-2.5 text-[10px] tracking-[0.18em] gap-1.5',
    lg: 'h-8 px-3 text-[11px] tracking-[0.2em] gap-1.5',
  }[size] || 'h-6 px-2.5 text-[10px] tracking-[0.18em] gap-1.5';

  const iconSize = size === 'lg' ? 'w-3.5 h-3.5' : size === 'sm' ? 'w-2.5 h-2.5' : 'w-3 h-3';

  const Tag = as;

  return (
    <Tag
      {...rest}
      className={`inline-flex items-center rounded-full border uppercase font-bold ${sizeCls} ${theme.badgeClass} ${className}`}
    >
      <Icon className={iconSize} />
      {showName ? theme.label : null}
    </Tag>
  );
}

/**
 * Bigger plan card — typically inserted in the sidebar footer / account
 * drawer. Combines badge, plan name, and a contextual CTA.
 */
export function PlanCard({ className = '' }) {
  const { plan, theme, isEnterprise } = usePlan();
  if (!plan) return null;
  const Icon = ICONS[theme.key] || Sparkles;

  return (
    <div
      className={`relative overflow-hidden rounded-2xl p-4 border ${
        isEnterprise
          ? 'bg-gradient-to-br ' + theme.gradient + ' text-white border-transparent'
          : 'bg-white border-[#E4E4E7]'
      } ${className}`}
      data-testid="plan-card"
    >
      {isEnterprise && (
        <div aria-hidden className="absolute -top-8 -right-8 w-32 h-32 rounded-full bg-white/10 blur-2xl" />
      )}
      <div className="relative flex items-center gap-2.5">
        <span
          className={`w-9 h-9 rounded-xl flex items-center justify-center ring-2 ${
            isEnterprise ? 'ring-white/30 bg-white/10' : `bg-gradient-to-br ${theme.softGradient} ${theme.ringClass}`
          }`}
        >
          <Icon className={`w-4 h-4 ${isEnterprise ? 'text-white' : ''}`} style={{ color: isEnterprise ? undefined : theme.accent }} />
        </span>
        <div className="min-w-0 flex-1">
          <p className={`text-[9.5px] tracking-[0.2em] uppercase font-bold ${isEnterprise ? 'text-white/60' : 'text-[#A1A1AA]'}`}>
            Aktiver Plan
          </p>
          <p className={`text-[14px] font-display font-black tracking-tight ${isEnterprise ? 'text-white' : 'text-[#09090B]'}`}>
            {plan.plan_name || theme.name}
          </p>
        </div>
      </div>
      {!isEnterprise && (
        <Link
          to="/pricing"
          className="mt-3 inline-flex w-full items-center justify-center gap-1.5 h-8 rounded-full bg-[#09090B] text-white text-[11px] font-bold tracking-tight hover:bg-[#0F8A5F] transition-colors"
          data-testid="plan-card-upgrade"
        >
          Upgrade ansehen
        </Link>
      )}
      {isEnterprise && (
        <Link
          to="/portal/enterprise"
          className="mt-3 inline-flex w-full items-center justify-center gap-1.5 h-8 rounded-full bg-white text-[#09090B] text-[11px] font-bold tracking-tight hover:bg-[#CFEEFF] transition-colors"
          data-testid="plan-card-enterprise-cta"
        >
          Enterprise-Dashboard öffnen
        </Link>
      )}
    </div>
  );
}
